import {
  OAUTH_PROVIDER_ORDER,
  OAuthProvider,
} from '@/constants/auth-providers';

/**
 * Remembers how a person last got into the dashboard so the sign-in screen can
 * point them back at it. Deliberately NOT derived from the email domain: a
 * @yahoo.com address can sit on a Google account, a corporate domain usually
 * sits on Microsoft 365 regardless of how the address looks, and a @gmail.com
 * one can sit on a password account. The only trustworthy sources are what
 * worked last time and Clerk's supportedFirstFactors for that account.
 *
 * A provider redirect cannot be recorded when the button is clicked - someone
 * who bounces off Google's consent screen never signed in, and a badge saying
 * otherwise sends them back to a door that did not open. So the click only
 * marks an attempt, and ProtectedRoute promotes it once a session exists.
 */

export type AuthMethod = 'password' | 'email_code' | OAuthProvider;

const STORAGE_KEY = 'ff:last-auth-method';
const PENDING_KEY = 'ff:pending-auth-method';

const isAuthMethod = (value: string | null): value is AuthMethod =>
  value === 'password' ||
  value === 'email_code' ||
  OAUTH_PROVIDER_ORDER.includes(value as OAuthProvider);

export const rememberAuthMethod = (method: AuthMethod): void => {
  try {
    window.localStorage.setItem(STORAGE_KEY, method);
  } catch {
    // Private browsing or blocked storage - the badge is a convenience only.
  }
};

export const getLastAuthMethod = (): AuthMethod | null => {
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    return isAuthMethod(stored) ? stored : null;
  } catch {
    return null;
  }
};

/** Marks a provider redirect as started. Not yet a successful sign-in. */
export const markAuthMethodAttempt = (method: AuthMethod): void => {
  try {
    window.sessionStorage.setItem(PENDING_KEY, method);
  } catch {
    // Ignored for the same reason as above.
  }
};

/** Turns a started attempt into the remembered method. Call once signed in. */
export const confirmAuthMethodAttempt = (): void => {
  try {
    const pending = window.sessionStorage.getItem(PENDING_KEY);
    if (!isAuthMethod(pending)) return;

    window.sessionStorage.removeItem(PENDING_KEY);
    rememberAuthMethod(pending);
  } catch {
    // Ignored for the same reason as above.
  }
};
